import * as React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link } from "react-router";
import { apiFetch, APIError, NetworkError } from "@/lib/api-client";

/**
 * Login-Route — Schritt 1 des magic-link-flows.
 *
 *  1. user gibt email ein
 *  2. POST /auth/magic-link {email}
 *  3. server schickt mail (oder simuliert via stdout-mailer im dev)
 *  4. wir zeigen success-screen "Email gesendet"
 *
 * Important: das endpoint antwortet IMMER 200 (enumeration-safe). Der
 * UI-state wechselt also nach send unabhängig davon ob die email bekannt
 * ist — das ist gewollt.
 *
 * Session-Ablauf-Kommunikation:
 *  - Fall A (reason:"expired"): sessionStorage-flag "hp:logout_reason" gesetzt
 *    → zeigt Ablauf-Banner + erklärender Text
 *  - Fall B (storage weg / neues Gerät): kein flag → normaler Login-Screen
 */

const loginSchema = z.object({
  email: z
    .string()
    .min(3, "Bitte E-Mail-Adresse eingeben")
    .max(254, "E-Mail-Adresse ist zu lang")
    .email("Ungültige E-Mail-Adresse"),
});

type LoginFormData = z.infer<typeof loginSchema>;

/** Resend-cooldown in Sekunden nach dem Absenden. */
const RESEND_COOLDOWN_S = 30;

export function LoginRoute(): React.ReactElement {
  const [submittedEmail, setSubmittedEmail] = React.useState<string | null>(null);
  const [serverError, setServerError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);

  // Ablauf-Banner: sessionStorage-flag lesen + sofort löschen
  const [sessionExpired] = React.useState<boolean>(() => {
    try {
      const reason = sessionStorage.getItem("hp:logout_reason");
      if (reason === "expired") {
        sessionStorage.removeItem("hp:logout_reason");
        return true;
      }
    } catch {
      // sessionStorage nicht verfügbar (private mode etc.) — ignorieren
    }
    return false;
  });

  // Resend-Cooldown-Timer (30s nach erstem Senden)
  const [resendSecondsLeft, setResendSecondsLeft] = React.useState(0);
  const resendTimerRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  const startResendCooldown = React.useCallback(() => {
    setResendSecondsLeft(RESEND_COOLDOWN_S);
    resendTimerRef.current = setInterval(() => {
      setResendSecondsLeft((prev) => {
        if (prev <= 1) {
          if (resendTimerRef.current) {
            clearInterval(resendTimerRef.current);
            resendTimerRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  React.useEffect(() => {
    return () => {
      if (resendTimerRef.current) clearInterval(resendTimerRef.current);
    };
  }, []);

  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "" },
  });

  const sendMagicLink = React.useCallback(
    async (email: string) => {
      setServerError(null);
      setSubmitting(true);
      try {
        await apiFetch("/auth/magic-link", {
          method: "POST",
          anonymous: true,
          body: { email },
        });
        setSubmittedEmail(email);
        startResendCooldown();
      } catch (err) {
        if (err instanceof NetworkError) {
          setServerError("Keine Verbindung zum Server. Bitte Internetverbindung prüfen.");
        } else if (err instanceof APIError && err.status === 429) {
          const retryHint = err.retryAfter
            ? ` Bitte in ${err.retryAfter} Sekunden erneut versuchen.`
            : "";
          setServerError(`Zu viele Anfragen.${retryHint}`);
        } else {
          setServerError("Es ist ein Fehler aufgetreten. Bitte später erneut versuchen.");
        }
      } finally {
        setSubmitting(false);
      }
    },
    [startResendCooldown],
  );

  const onSubmit = async (data: LoginFormData) => {
    await sendMagicLink(data.email);
  };

  const onResend = async () => {
    if (!submittedEmail || resendSecondsLeft > 0) return;
    await sendMagicLink(submittedEmail);
  };

  if (submittedEmail) {
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col justify-center gap-6 px-6 py-10">
        <header className="space-y-2">
          <h1 className="text-3xl font-semibold">E-Mail gesendet</h1>
          <p className="text-neutral-600">
            Wir haben einen Anmelde-Link an{" "}
            <strong className="text-neutral-900">{submittedEmail}</strong> gesendet.
          </p>
        </header>

        <section className="rounded-lg bg-[color:var(--color-surface-elevated)] p-6 space-y-3">
          <p className="text-sm text-neutral-700">
            Die E-Mail trifft meist innerhalb einer Minute ein.
          </p>
          <p className="text-sm text-neutral-700">
            Öffnen Sie die E-Mail{" "}
            <strong className="font-semibold">am besten auf diesem Gerät</strong> und tippen Sie auf
            den Anmelde-Link. Der Link ist 15 Minuten gültig und nur einmal verwendbar.
          </p>
          <p className="text-xs text-neutral-500">
            Keine E-Mail erhalten? Bitte auch den Spam- oder Werbung-Ordner prüfen.
          </p>
        </section>

        {serverError && (
          <p
            role="alert"
            className="rounded-md bg-[color:var(--color-heat-4-soft)] px-3 py-2 text-sm text-[color:var(--color-text-primary)]"
          >
            {serverError}
          </p>
        )}

        <div className="flex flex-col gap-3">
          {/* Primär-sekundär: gleiche Adresse nochmal senden, mit 30s-Cooldown */}
          <button
            type="button"
            onClick={() => void onResend()}
            disabled={resendSecondsLeft > 0 || submitting}
            aria-label={
              resendSecondsLeft > 0
                ? `Anmelde-Link erneut senden, verfügbar in ${resendSecondsLeft} Sekunden`
                : "Anmelde-Link erneut senden"
            }
            className="rounded-md border border-[color:var(--color-border)] px-4 py-3 text-sm font-medium text-neutral-700 hover:bg-[color:var(--color-surface-sunken)] disabled:opacity-50"
          >
            {resendSecondsLeft > 0
              ? `Erneut senden in ${resendSecondsLeft} s`
              : "Anmelde-Link erneut senden"}
          </button>

          {/* Tertiär: andere Adresse verwenden */}
          <button
            type="button"
            onClick={() => {
              setSubmittedEmail(null);
              setServerError(null);
              form.reset();
            }}
            className="rounded-md border border-[color:var(--color-border)] px-4 py-3 text-sm font-medium text-neutral-500 hover:bg-[color:var(--color-surface-sunken)]"
          >
            Andere E-Mail-Adresse verwenden
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="mx-auto flex min-h-screen max-w-md flex-col justify-center gap-6 px-6 py-10">
      <header className="space-y-2">
        <h1 className="text-3xl font-semibold">Anmelden</h1>
        <p className="text-neutral-600">
          Sie erhalten einen einmaligen Anmelde-Link per E-Mail. Kein Passwort nötig.
        </p>
      </header>

      {/* Ablauf-Banner (Fall A) — role="status" da Abmeldung kein Fehler ist */}
      {sessionExpired && (
        <div
          role="status"
          className="rounded-md border-l-4 border-[color:var(--color-info)] bg-[color:var(--color-cool-teal-50)] px-4 py-3"
        >
          <p className="text-base font-semibold text-[color:var(--color-text-primary)]">
            Ihre Sitzung ist abgelaufen.
          </p>
          <p className="mt-1 text-sm text-[color:var(--color-text-secondary)]">
            Aus Sicherheitsgründen melden Sie sich nach längerer Zeit einmalig neu an. Geben Sie
            Ihre E-Mail-Adresse ein — Sie bleiben danach wieder dauerhaft angemeldet.
          </p>
        </div>
      )}

      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col gap-4"
        noValidate
        aria-describedby={serverError ? "login-error" : undefined}
      >
        <div className="flex flex-col gap-1">
          <label htmlFor="email" className="text-sm font-medium text-neutral-700">
            E-Mail-Adresse
          </label>
          <input
            id="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            placeholder="ihr.name@schule.de"
            disabled={submitting}
            aria-invalid={!!form.formState.errors.email}
            aria-describedby={form.formState.errors.email ? "email-error" : undefined}
            className="h-12 rounded-md border border-[color:var(--color-border)] bg-white px-3 text-base text-neutral-900 focus:border-[color:var(--color-border-focus)] focus:outline-none disabled:opacity-50"
            {...form.register("email")}
          />
          {form.formState.errors.email && (
            <p id="email-error" role="alert" className="text-sm text-[color:var(--color-danger)]">
              {form.formState.errors.email.message}
            </p>
          )}
        </div>

        {serverError && (
          <p
            id="login-error"
            role="alert"
            className="rounded-md bg-[color:var(--color-heat-4-soft)] px-3 py-2 text-sm text-[color:var(--color-text-primary)]"
          >
            {serverError}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="h-12 rounded-md bg-[color:var(--color-brand-primary-strong)] px-6 font-semibold text-white transition-colors hover:bg-[color:var(--color-warm-orange-800)] disabled:opacity-50"
        >
          {submitting ? "Anmelde-Link wird gesendet …" : "Anmelde-Link senden"}
        </button>

        {/* Vertrauensbildende Zeile — kein Control, nur Information */}
        <p className="mt-1 text-sm text-[color:var(--color-text-secondary)]">
          Sie bleiben auf diesem Gerät angemeldet — eine erneute Anmeldung ist nur selten nötig.
        </p>
      </form>

      <footer className="text-xs text-neutral-500">
        <p>
          Mit der Anmeldung stimmen Sie der{" "}
          <Link
            to="https://hitzeprotokoll.de/datenschutz"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[color:var(--color-brand-accent)] underline"
          >
            Datenschutzerklärung
          </Link>{" "}
          zu.
        </p>
      </footer>
    </main>
  );
}
